// Data Product Catalog — public marketplace cho final tables + use cases (Phase 0 POC 2026-05-21)
import { useEffect, useState } from 'react';
import {
    Box, Card, CardContent, Chip, Container, Typography, Button, Stack,
    CircularProgress, Alert, Grid, Tooltip
} from '@mui/material';
import { IconBrandGoogle, IconDownload, IconTag, IconLicense, IconBookmark } from '@tabler/icons-react';
import axios from 'axios';

const PUBLIC_API = process.env.REACT_APP_API_URL || '/api/v1';

export default function CatalogPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Anonymous fetch — no Authorization header
        axios.get(`${PUBLIC_API}/data-products`)
            .then((r) => setProducts(r.data.products || []))
            .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Load catalog failed'))
            .finally(() => setLoading(false));
    }, []);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                    Data Product Catalog
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 800 }}>
                    Final tables, use cases, models công bố từ AI Lab DataHub. Mỗi product có notebook
                    Colab tự động — click "Open in Colab" để bắt đầu khám phá ngay.
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}

            {!loading && !error && products.length === 0 && (
                <Alert severity="info">Chưa có Data Product nào được publish.</Alert>
            )}

            <Grid container spacing={2.5}>
                {products.map((p) => (
                    <Grid item xs={12} md={6} key={p.slug}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent sx={{ flex: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    <Chip
                                        size="small"
                                        label={p.type}
                                        color={p.type === 'use_case' ? 'primary' : 'default'}
                                        sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`v${p.version}`}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: 11 }}
                                    />
                                    {p.requires_colab_pro && (
                                        <Chip
                                            size="small"
                                            label="Colab Pro"
                                            color="warning"
                                            sx={{ height: 20, fontSize: 11 }}
                                        />
                                    )}
                                </Stack>

                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                    {p.name}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                                    {p.description}
                                </Typography>

                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                    <Tooltip title="License">
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <IconLicense size={14} color="#9e9e9e" />
                                            <Typography variant="caption" color="text.secondary">{p.license}</Typography>
                                        </Stack>
                                    </Tooltip>
                                    <Tooltip title="Files">
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <IconBookmark size={14} color="#9e9e9e" />
                                            <Typography variant="caption" color="text.secondary">{p.file_count} files</Typography>
                                        </Stack>
                                    </Tooltip>
                                </Stack>

                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                    {(p.tags || []).map((tag) => (
                                        <Chip
                                            key={tag}
                                            size="small"
                                            icon={<IconTag size={11} />}
                                            label={tag}
                                            sx={{ height: 20, fontSize: 10, bgcolor: 'grey.100' }}
                                        />
                                    ))}
                                </Stack>
                            </CardContent>

                            <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<IconBrandGoogle size={16} />}
                                    href={p.colab_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ flex: 1, textTransform: 'none' }}
                                >
                                    Open in Colab
                                </Button>
                                <Tooltip title="Download .ipynb file">
                                    <Button
                                        variant="outlined"
                                        startIcon={<IconDownload size={16} />}
                                        href={p.notebook_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Notebook
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    <strong>Hướng dẫn sử dụng:</strong> Mỗi product gắn với 1 project DataHub. Để chạy
                    notebook trong Colab, bạn cần tạo <strong>API Key</strong> từ tab "API Gateway" của
                    project tương ứng (yêu cầu tài khoản DataHub), sau đó dán key vào{' '}
                    <strong>Colab Secrets</strong> dưới tên <code>DATAHUB_API_KEY</code>. Phase 1 sẽ
                    bổ sung tier anonymous + auto-issue key cho consumer mới.
                </Typography>
            </Box>
        </Container>
    );
}
